import { Building2 } from 'lucide-react';
import type { ReactElement, ReactNode } from 'react';

import { BackLink } from '@/components/layout/BackLink';
import { TO_PANEL } from '@/lib/back-nav';

import { PermissionErrorPanel } from '@/components/auth/PermissionErrorPanel';
import { GatedChrome, RestrictedPanel } from '@/components/auth/GatedChrome';
import { StatePanel } from '@/components/ui/StatePanel';
import { usePermission, usePermissionState } from '@/hooks/usePermission';
import { useActiveCompany } from '@/lib/active-company-context';
import { useMe } from '@/lib/me';

import { PrintLogCard } from './components/PrintLogCard';
import { WatermarkPanel, WatermarkPanelSkeleton } from './components/WatermarkPanel';

const BREADCRUMB = [
  { label: 'QMS', to: '/dashboard', strong: true },
  { label: 'Marca de agua e impresión', strong: true },
];

export const PRINT_PERMISSION = 'quality:document:print';
export const CONFIG_PERMISSION = 'quality:document:watermark-config';
export const LOG_PERMISSION = 'quality:print-log:read';

function Frame({ children }: { children: ReactNode }): ReactElement {
  // G4: sin un permiso de la pantalla confirmado, las migas y el encabezado no la nombran.
  const print = usePermission(PRINT_PERMISSION);
  const config = usePermission(CONFIG_PERMISSION);
  const log = usePermission(LOG_PERMISSION);
  const state = print || config || log ? 'granted' : 'denied';
  return (
    <>
      <GatedChrome state={state} breadcrumb={BREADCRUMB} navContext="Marca de agua e impresión" />
      <BackLink target={TO_PANEL} />
      {children}
    </>
  );
}

/** Pantalla Marca de agua e impresión (so=400): configuración de la marca con vista
 *  previa y bitácora de copias de toda la empresa, cada sección según su permiso. */
export function WatermarkScreen(): ReactElement {
  const print = usePermissionState(PRINT_PERMISSION);
  const canEdit = usePermission(CONFIG_PERMISSION);
  const canLog = usePermission(LOG_PERMISSION);
  const { activeCompanyId, activeCompanyName, isLoading: companyLoading } = useActiveCompany();
  const me = useMe().data?.data;
  const userName =
    [me?.first_name, me?.last_name].filter(Boolean).join(' ').trim() || me?.name || 'Usuario';
  const canView = print.state === 'granted' || canEdit;

  if (print.state === 'error') {
    return (
      <Frame>
        <PermissionErrorPanel error={print.error} onRetry={print.retry} retrying={print.retrying} />
      </Frame>
    );
  }
  if (print.state === 'loading') {
    return (
      <Frame>
        <div style={{ marginTop: 16 }}>
          <WatermarkPanelSkeleton label="Cargando" />
        </div>
      </Frame>
    );
  }
  if (!canView && !canLog) {
    return (
      <Frame>
        <RestrictedPanel />
      </Frame>
    );
  }
  if (!companyLoading && !activeCompanyId) {
    return (
      <Frame>
        <StatePanel icon={<Building2 />} title="Selecciona una empresa activa" role="status">
          Elige una empresa activa en la barra superior.
        </StatePanel>
      </Frame>
    );
  }

  return (
    <Frame>
      <div className="pagehead rise">
        <div className="grow">
          <h1 style={{ marginBottom: 4 }}>Marca de agua e impresión</h1>
          <p style={{ maxWidth: '70ch' }}>
            Leyendas y estilo de la marca de agua, y registro de impresiones y descargas.
          </p>
        </div>
      </div>
      {activeCompanyId !== null && (
        <div style={{ marginTop: 16 }}>
          {canView && (
            <WatermarkPanel
              companyId={activeCompanyId}
              companyName={activeCompanyName ?? ''}
              userName={userName}
              canEdit={canEdit}
            />
          )}
          {canLog && <PrintLogCard companyId={activeCompanyId} />}
        </div>
      )}
    </Frame>
  );
}
